// The re-entry screen: this browser can't open the quote as it arrived, and
// the visitor gets back in with a code or link, without needing to know
// whether they have an account. Shared by the pre-need and at-need quote
// pages through the quote-access flow. The server knows who a spent link
// was made for and offers its masked destinations (`on_file`); with no
// link to go on the visitor types where to send it (`typed`). Either way
// the code or link is the existing login-grant flow, and signing in hands
// back to the shared access flow, which opens the quote.

import { useEffect, useRef, useState } from "react";
import { PhoneIcon } from "@heroicons/react/24/outline";

import { LoginGrantConfirm } from "pages/Login/LoginGrantConfirm";
import {
  SESSION_ENDED_COPY,
  type GrantConfirmReport,
  type GrantLookup,
  type GrantMethod,
} from "pages/Login/login-grant-flow";
import { recordGrantSend } from "pages/Login/grant-send";
import ArrivalLoadingScreen from "./ArrivalLoadingScreen";
import { useQuoteFlowAnalytics, type QuoteType } from "./analytics";
import { useTrackLeadFlowEvent } from "pages/StartLead/analytics";
import {
  completionResultOf,
  signInFailureCopy,
  startResultOf,
} from "pages/StartLead/signIn";
import { startSignIn } from "pages/StartLead/signInClient";
import { leadFlowEvents, type ConfirmTrigger } from "pages/StartLead/events";

import { interstitialCopy, refusalNote } from "./interstitial-copy";
import {
  NO_REENTRY_OPTIONS,
  NOT_FOUND_COPY,
  firstSendTrigger,
  leadingMethodOf,
  proofOutcomeOf,
  recipientLookupToken,
  reentryFailureCopy,
  reentryModeOf,
  reentryPath,
  reentryViewEntryFor,
  type ProofOutcome,
  type ReentryMode,
  type ReentryOptions,
} from "./reentry";
import { fetchReentryOptions, sendReentry } from "./reentry-client";
import { reentryEvents, type ReentryEvent } from "./reentry-events";
import { OnFileChoice, TypedForm } from "./ReentryChoice";
import {
  CallButton,
  ProofOutcomeView,
  ReentryFrame,
  TAP_LINK_CLASS,
} from "./ReentryParts";

export interface ReentryScreenProps {
  quoteId: string;
  /** The 409 body's `error` code, or "no_credential" for a bare arrival. */
  reason: string;
  /** The URL's one-time link as it arrived, which the address bar no
   * longer holds. */
  linkToken: string | null;
  subject: QuoteType;
  /** The quote stayed closed for the account the visitor just signed in
   * as: the email or phone they proved reaches nobody who may open it. */
  closedAfterProof: boolean;
  /** A texted code has signed the visitor in; the quote is next. */
  onSignedIn: () => void;
  /** The line to call. */
  supportLine: { href: string; label: string };
}

type Phase =
  | { kind: "choose" }
  | {
      kind: "code";
      loginGrant: string;
      lookup: GrantLookup;
      /** The method the visitor picked, when the grant offers more. */
      chosen: GrantMethod | null;
    }
  | { kind: "opening" }
  | { kind: "outcome"; outcome: ProofOutcome };

function useReentryOptions(
  quoteId: string,
  token: string | null,
): ReentryOptions | null {
  const { reportFailure } = useQuoteFlowAnalytics();
  const [fetched, setFetched] = useState<ReentryOptions | null>(null);
  useEffect(() => {
    if (token === null) return;
    let live = true;
    void fetchReentryOptions(quoteId, token, (error) =>
      reportFailure("reentry_options", error),
    ).then((options) => {
      if (live) setFetched(options);
    });
    return () => {
      live = false;
    };
    // The options are asked for once per link.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteId, token]);
  return token === null ? NO_REENTRY_OPTIONS : fetched;
}

function ChoicePanel({
  phase,
  mode,
  options,
  sending,
  looking,
  notice,
  prefill,
  onChoose,
  onSubmitTyped,
  onRetry,
}: {
  phase: Phase;
  mode: ReentryMode;
  options: ReentryOptions;
  sending: GrantMethod | null;
  looking: boolean;
  notice: string | null;
  prefill: string;
  onChoose: (method: GrantMethod) => void;
  onSubmitTyped: (identifier: string) => void;
  onRetry: () => void;
}) {
  if (phase.kind === "outcome") {
    return <ProofOutcomeView outcome={phase.outcome} onRetry={onRetry} />;
  }
  if (mode === "on_file") {
    return (
      <OnFileChoice
        options={options}
        busy={sending}
        error={notice}
        onChoose={onChoose}
      />
    );
  }
  return (
    <TypedForm
      notice={notice}
      initialValue={prefill}
      busy={looking}
      onSubmit={onSubmitTyped}
    />
  );
}

export default function ReentryScreen(props: ReentryScreenProps) {
  const token = recipientLookupToken(props.reason, props.linkToken);
  const options = useReentryOptions(props.quoteId, token);
  if (options === null) {
    return <ArrivalLoadingScreen />;
  }
  return <ReentryContent {...props} token={token} options={options} />;
}

function ReentryContent({
  quoteId,
  reason,
  subject,
  closedAfterProof,
  onSignedIn,
  supportLine,
  token,
  options: initialOptions,
}: ReentryScreenProps & { token: string | null; options: ReentryOptions }) {
  const { track, reportFailure } = useQuoteFlowAnalytics();
  const trackEvent = useTrackLeadFlowEvent();
  const [options, setOptions] = useState(initialOptions);
  const [refreshing, setRefreshing] = useState(false);
  const onFileMode = reentryModeOf(options);
  const [mode, setMode] = useState<ReentryMode>(
    closedAfterProof ? "typed" : onFileMode,
  );
  const [phase, setPhase] = useState<Phase>({ kind: "choose" });
  const [sending, setSending] = useState<GrantMethod | null>(null);
  const [looking, setLooking] = useState(false);
  const [notice, setNotice] = useState<string | null>(
    closedAfterProof ? NOT_FOUND_COPY : null,
  );
  const [prefill, setPrefill] = useState("");
  const typed = useRef("");
  const firstTrigger = useRef<ConfirmTrigger | null>(null);
  const leading = leadingMethodOf(options);

  const emit = ({ name, properties }: ReentryEvent) => track(name, properties);

  const viewedMode = useRef<ReentryMode | null>(null);
  useEffect(() => {
    if (viewedMode.current === mode) return;
    const entry = reentryViewEntryFor(viewedMode.current);
    viewedMode.current = mode;
    emit(reentryEvents.viewed({ mode, reason, entry }));
    // One report per mode the visitor is shown.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const reportedClosed = useRef(false);
  useEffect(() => {
    if (!closedAfterProof || reportedClosed.current) return;
    reportedClosed.current = true;
    emit(reentryEvents.notFound());
    setMode("typed");
    setNotice(NOT_FOUND_COPY);
    setPhase({ kind: "choose" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closedAfterProof]);

  const report: GrantConfirmReport = {
    sent: (method, trigger) => {
      const first = trigger === "automatic" ? firstTrigger.current : null;
      firstTrigger.current = null;
      emit(
        reentryEvents.sent({
          method,
          mode,
          trigger: first ?? trigger,
        }),
      );
    },
    accepted: (method) => emit(reentryEvents.accepted({ method })),
    failed: (failure) => emit(reentryEvents.failed({ reason: failure })),
  };

  const startCode = (
    loginGrant: string,
    lookup: GrantLookup,
    chosen: GrantMethod | null,
  ) => {
    setNotice(null);
    setPhase({ kind: "code", loginGrant, lookup, chosen });
  };

  const chooseOnFile = async (method: GrantMethod) => {
    if (sending !== null || token === null) return;
    setSending(method);
    setNotice(null);
    const answer = await sendReentry({
      quoteId,
      token,
      method,
      options,
      report: (error) => reportFailure("reentry_send", error),
    });
    setSending(null);
    if (answer.kind === "failed") {
      emit(reentryEvents.failed({ reason: answer.failure }));
      setNotice(reentryFailureCopy(answer.failure));
      if (answer.failure === "link_unknown") setMode("typed");
      return;
    }
    const trigger = firstSendTrigger(method, leading);
    if (answer.sent) {
      recordGrantSend(answer.loginGrant, answer.sent, Date.now());
      emit(reentryEvents.sent({ method, mode: "on_file", trigger }));
    } else {
      firstTrigger.current = trigger;
    }
    startCode(answer.loginGrant, answer.lookup, method);
  };

  const submitTyped = async (identifier: string) => {
    if (looking) return;
    setLooking(true);
    setNotice(null);
    typed.current = identifier;
    trackEvent(leadFlowEvents.signInStarted("look_up", "reentry"));
    const answer = await startSignIn(identifier, reentryPath(quoteId));
    setLooking(false);
    trackEvent(
      leadFlowEvents.signInResult("look_up", "reentry", startResultOf(answer)),
    );
    if (answer.kind === "failed") {
      emit(reentryEvents.failed({ reason: answer.failure }));
      setNotice(signInFailureCopy(answer.failure));
      return;
    }
    firstTrigger.current = "automatic";
    startCode(answer.loginGrant, answer.lookup, null);
  };

  const backToChoice = (
    notice: string | null = null,
    identifier: string = "",
  ) => {
    setNotice(notice);
    setPrefill(identifier);
    setPhase({ kind: "choose" });
  };

  const chooseDifferent = () => {
    trackEvent(leadFlowEvents.useDifferentClicked("reentry"));
  };

  const refreshOptions = async () => {
    if (token === null) return;
    setRefreshing(true);
    const next = await fetchReentryOptions(quoteId, token, (error) =>
      reportFailure("reentry_options", error),
    );
    setOptions(next);
    setMode((current) =>
      current === "on_file" ? reentryModeOf(next) : current,
    );
    setRefreshing(false);
  };

  if (phase.kind === "opening" || refreshing) {
    return <ArrivalLoadingScreen />;
  }

  if (phase.kind === "code") {
    return (
      <ReentryFrame>
        <div className="w-full text-left">
          <LoginGrantConfirm
            key={phase.loginGrant}
            grant={{
              loginGrant: phase.loginGrant,
              leadKind: subject === "case" ? "death" : "planning",
              ...(phase.chosen === null ? {} : { method: phase.chosen }),
            }}
            lookup={phase.lookup}
            surface="reentry"
            embedded
            report={report}
            onInvalid={(stage) => {
              const fromTyped = mode === "typed";
              trackEvent(
                leadFlowEvents.sessionEndedViewed({
                  purpose: phase.lookup.purpose,
                  surface: "reentry",
                  stage,
                  resolution: fromTyped ? "look_up" : "renew",
                }),
              );
              backToChoice(SESSION_ENDED_COPY, fromTyped ? typed.current : "");
              if (!fromTyped) void refreshOptions();
            }}
            onChangeDestination={() => {
              chooseDifferent();
              backToChoice();
            }}
            onCompletion={(completion) => {
              if (completion.kind === "use_account_channel") {
                trackEvent(
                  leadFlowEvents.signInResult(
                    "look_up",
                    "reentry",
                    completionResultOf(completion),
                  ),
                );
              }
              const outcome = proofOutcomeOf(completion);
              if (outcome.kind === "not_found") emit(reentryEvents.notFound());
              setPhase({ kind: "outcome", outcome });
            }}
            onSignedIn={() => {
              setPhase({ kind: "opening" });
              onSignedIn();
            }}
          />
        </div>
      </ReentryFrame>
    );
  }

  const copy = interstitialCopy(reason, mode);
  const note = mode === "typed" ? refusalNote(reason) : null;

  return (
    <ReentryFrame>
      <div className="flex h-12 w-12 flex-none items-center justify-center rounded-full bg-oat">
        <PhoneIcon
          className="h-5 w-5 text-evergreen"
          strokeWidth={2}
          aria-hidden="true"
        />
      </div>
      <h1 className="font-serif text-[25px] leading-tight text-soil sm:text-[31px]">
        {copy.title}
      </h1>
      <p className="text-base leading-relaxed text-oat-700 sm:text-lg">
        {copy.body}
      </p>
      <ChoicePanel
        phase={phase}
        mode={mode}
        options={options}
        sending={sending}
        looking={looking}
        notice={notice}
        prefill={prefill}
        onChoose={(method) => void chooseOnFile(method)}
        onSubmitTyped={(identifier) => void submitTyped(identifier)}
        onRetry={() => {
          chooseDifferent();
          setMode("typed");
          backToChoice();
        }}
      />
      {note && <p className="text-base text-oat-700">{note}</p>}
      <div className="flex flex-col items-center gap-3">
        {mode === "on_file" && (
          <p className="text-base text-oat-700">
            Not you?{" "}
            <button
              type="button"
              className={TAP_LINK_CLASS}
              onClick={() => {
                chooseDifferent();
                setMode("typed");
              }}
            >
              Use a different email or phone
            </button>
          </p>
        )}
        {mode === "typed" && onFileMode === "on_file" && !closedAfterProof && (
          <button
            type="button"
            className={TAP_LINK_CLASS}
            onClick={() => setMode("on_file")}
          >
            Back
          </button>
        )}
        <CallButton
          line={supportLine}
          track={{ surface: "reentry", screen: "reentry" }}
        />
      </div>
    </ReentryFrame>
  );
}
